import type { T3ActivityItem } from '@shared/agent-protocol';
import { turnStartIndex } from '@/components/factory/agent-activity';
import type { ChatSessionSummary } from '@shared/types/chat';
import { ymdInZone } from '@shared/wall-clock';
import { ymdAddDays } from '@/lib/ymd';

/** T-54: the chat screen's sums (the sidebar's day groups, following the conversation, the live answer). */

export interface ChatDayGroup {
  label: 'Today' | 'Yesterday' | 'Previous 7 days' | 'Older';
  sessions: ChatSessionSummary[];
}

/**
 * The sidebar's groups by each session's last message, in `timeZone` (the project's, or UTC; never the
 * browser's): Today, Yesterday, Previous 7 days, Older. Keeps the list's order; empty groups are left out.
 */
export function groupSessionsByDay(sessions: readonly ChatSessionSummary[], now: Date, timeZone: string): ChatDayGroup[] {
  const today = ymdInZone(now, timeZone);
  const yesterday = ymdAddDays(today, -1);
  const weekAgo = ymdAddDays(today, -7);
  const groups: ChatDayGroup[] = [
    { label: 'Today', sessions: [] },
    { label: 'Yesterday', sessions: [] },
    { label: 'Previous 7 days', sessions: [] },
    { label: 'Older', sessions: [] },
  ];
  for (const s of sessions) {
    const at = new Date(s.last_message_at);
    const day = Number.isNaN(at.getTime()) ? '' : ymdInZone(at, timeZone);
    // A clock a little ahead of ours still counts as today.
    const index = day >= today ? 0 : day === yesterday ? 1 : day >= weekAgo ? 2 : 3;
    groups[index]!.sessions.push(s);
  }
  return groups.filter((g) => g.sessions.length);
}

/** Older pages folded under the first (which is read again on each poll): each session once, the fresher copy winning. */
export function mergeSessionPages(first: readonly ChatSessionSummary[], older: readonly ChatSessionSummary[]): ChatSessionSummary[] {
  const seen = new Set(first.map((s) => s.id));
  return [...first, ...older.filter((s) => !seen.has(s.id))];
}

/** How close to the bottom (px) still counts as reading the newest message, so new ones are followed. */
export const FOLLOW_SLACK_PX = 80;

export function isNearBottom(el: { scrollTop: number; scrollHeight: number; clientHeight: number }): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK_PX;
}

/** What the agent has written so far this turn, read from its activity. */
export interface LiveAnswer {
  itemId: string;
  text: string;
  /** Only a preview came back: read the item for the rest. */
  truncated: boolean;
  /** Still being written. */
  writing: boolean;
  /** Changes when the item does, to read its full text again only then. */
  version: string;
}

/**
 * The agent's latest message since the person's last one (T-54): the answer as it's written. Items are
 * oldest first; a turn without a user message in the items read counts from the start. Null before the
 * agent has written anything this turn.
 */
export function liveAnswerFrom(items: readonly T3ActivityItem[]): LiveAnswer | null {
  let start = 0;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i]!.type === 'user_message') {
      start = i + 1;
      break;
    }
  }
  for (let i = items.length - 1; i >= start; i--) {
    const item = items[i]!;
    if (item.type !== 'assistant_message' || !item.text?.trim()) continue;
    return {
      itemId: item.itemId,
      text: item.text,
      truncated: item.textTruncated,
      writing: item.status === 'in_progress',
      version: `${item.position}:${item.updatedAt ?? ''}:${item.text.length}:${item.status ?? ''}`,
    };
  }
  return null;
}

/** "Make this a task" (T-54): the reply, then where it came from. The chat itself is only its owner's to open. */
export function taskFromReply(reply: string, chat: { title: string }): { title: string; text: string } {
  return { title: chat.title, text: `${reply.trim()}\n\n_From the chat "${chat.title}"._` };
}

/**
 * Whether `text` names the task `ref` as a whole ref ("SF-T-13" in "about SF-T-13," but not in "SF-T-137"
 * or "XSF-T-13"), for New chat's task (SF-T-136): a prefix would pick another task's worktree.
 */
export function mentionsTaskRef(text: string, ref: string): boolean {
  if (!ref) return false;
  const escaped = ref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?<![A-Za-z0-9-])${escaped}(?![0-9A-Za-z])`, 'i').test(text);
}

/** SF-T-266: the time an item was last updated (ms), or NaN without one. */
const itemTime = (item: T3ActivityItem) => (item.updatedAt ? Date.parse(item.updatedAt) : NaN);

/** SF-T-266: what the agent did on the way to an answer, as the thread lists it: no prompts, no file lists (the answer's own changes card has those). */
const isTurnWork = (item: T3ActivityItem) => item.type !== 'user_message' && item.type !== 'file_change' && item.type !== 'checkpoint';

/**
 * SF-T-266: one past chat turn's work, from the thread's items: those updated after `since` (the previous answer, or
 * the chat's start) up to `until` (this answer), less the prompts, the file lists and the answer itself (its last
 * message, which the chat already shows).
 */
export function turnWorkItems(items: readonly T3ActivityItem[], since: number, until: number): T3ActivityItem[] {
  const inTurn = items.filter((item) => {
    const at = itemTime(item);
    return at > since && at <= until && isTurnWork(item);
  });
  let answer = -1;
  inTurn.forEach((item, i) => {
    if (item.type === 'assistant_message') answer = i;
  });
  return inTurn.filter((_, i) => i !== answer);
}

/** SF-T-266: the work of the turn going now: what came after the last prompt, less the answer being written. */
export function liveTurnWork(items: readonly T3ActivityItem[], answerItemId: string | null): T3ActivityItem[] {
  // After the turn's prompt; a message steered in stays in the turn (SF-T-266).
  return items.slice(turnStartIndex(items)).filter((item) => isTurnWork(item) && item.itemId !== answerItemId);
}
